import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { orderAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Zap, ShoppingCart, Package, ArrowLeft, CheckCircle, Minus, Plus, Trash2
} from 'lucide-react';

const CheckoutPage = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem('signa_cart') || '[]'); } catch { return []; }
  });
  const [formData, setFormData] = useState({
    customer_name: user?.name || '',
    email: user?.email || '',
    phone: '',
    shipping_address: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (cart.length === 0 && !orderPlaced) { navigate('/shop'); }
  }, [isAuthenticated, cart, navigate, orderPlaced]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({ ...prev, customer_name: prev.customer_name || user.name || '', email: prev.email || user.email || '' }));
    }
  }, [user]);

  const updateQuantity = (itemId, delta) => {
    const updated = cart.map(c => {
      if (c.item_id !== itemId) return c;
      const newQty = c.quantity + delta;
      return newQty > 0 ? { ...c, quantity: newQty } : c;
    }).filter(c => c.quantity > 0);
    setCart(updated);
    localStorage.setItem('signa_cart', JSON.stringify(updated));
  };

  const removeFromCart = (itemId) => {
    const updated = cart.filter(c => c.item_id !== itemId);
    setCart(updated);
    localStorage.setItem('signa_cart', JSON.stringify(updated));
  };

  const cartTotal = cart.reduce((sum, c) => sum + c.price * c.quantity, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_name || !formData.email || !formData.phone || !formData.shipping_address) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const res = await orderAPI.create({
        items: cart.map(c => ({ item_id: c.item_id, name: c.name, price: c.price, quantity: c.quantity, artifact_type: c.artifact_type })),
        ...formData,
      });
      setOrderPlaced(res.data);
      localStorage.removeItem('signa_cart');
      setCart([]);
      toast.success('Order placed successfully!');
    } catch (error) {
      toast.error('Failed to place order');
    } finally { setSubmitting(false); }
  };

  // Order success
  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="order-success">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md w-full text-center shadow-sm">
          <div className="w-16 h-16 bg-emerald-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Order Placed!</h1>
          <p className="text-slate-500 mb-4">Your order has been received. We'll process it shortly.</p>
          <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
            <p className="text-sm text-slate-500 mb-1">Order ID</p>
            <p className="font-mono text-sm font-semibold text-slate-900" data-testid="order-id">{orderPlaced.order_id}</p>
            <p className="text-sm text-slate-500 mt-3 mb-1">Total</p>
            <p className="text-xl font-bold text-slate-900">₱{orderPlaced.total_price?.toLocaleString()}</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => navigate('/dashboard/orders')} className="flex-1 bg-indigo-600 hover:bg-indigo-700" data-testid="view-orders-btn">View Orders</Button>
            <Button onClick={() => navigate('/shop')} variant="outline" className="flex-1">Continue Shopping</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="checkout-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center"><Zap className="w-5 h-5 text-white" /></div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Signa</span>
          </Link>
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back to Shop
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Checkout</h1>
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Shipping Details</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input className="mt-1" value={formData.customer_name} onChange={e => setFormData({ ...formData, customer_name: e.target.value })} required data-testid="checkout-name" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email *</Label>
                    <Input className="mt-1" type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required data-testid="checkout-email" />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input className="mt-1" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} required data-testid="checkout-phone" />
                  </div>
                </div>
                <div>
                  <Label>Shipping Address *</Label>
                  <Input className="mt-1" value={formData.shipping_address} onChange={e => setFormData({ ...formData, shipping_address: e.target.value })} required data-testid="checkout-address" />
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Input className="mt-1" value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} data-testid="checkout-notes" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-indigo-600 hover:bg-indigo-700 py-3 text-base font-semibold" data-testid="place-order-btn">
                  {submitting ? <div className="spinner w-5 h-5 border-2" /> : <>Place Order &mdash; ₱{cartTotal.toLocaleString()}</>}
                </Button>
              </form>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-24">
              <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Order Summary</h2>
              <div className="space-y-3 mb-4">
                {cart.map(item => (
                  <div key={item.item_id} className="flex items-center gap-3" data-testid={`summary-${item.item_id}`}>
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Package className="w-5 h-5 text-indigo-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{item.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <button onClick={() => updateQuantity(item.item_id, -1)} className="w-5 h-5 flex items-center justify-center rounded bg-slate-100 hover:bg-slate-200 text-slate-500"><Minus className="w-3 h-3" /></button>
                        <span className="text-xs font-medium">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.item_id, 1)} className="w-5 h-5 flex items-center justify-center rounded bg-slate-100 hover:bg-slate-200 text-slate-500"><Plus className="w-3 h-3" /></button>
                        <button onClick={() => removeFromCart(item.item_id)} className="text-slate-300 hover:text-red-500 ml-1"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </div>
                    <span className="text-sm font-medium text-slate-900">₱{(item.price * item.quantity).toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-200 pt-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-600">Total</span>
                  <span className="text-xl font-bold text-slate-900" data-testid="checkout-total">₱{cartTotal.toLocaleString()}</span>
                </div>
                <p className="text-xs text-slate-400 mt-2">Payment will be collected upon delivery. This is a virtual order.</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CheckoutPage;
